/**
 * Plugin Registry
 *
 * Central push-based registry. Plugin files call registerPlugin()
 * at module scope. Consumers query the registry for tools, slash
 * commands, and context menu entries.
 *
 * Import order: types.ts (leaf) ← registry.ts ← plugin files ← loader.ts ← consumers.
 * No circular imports.
 */

import type { ToolDefinition } from '../ai/tools/types';
import type {
    PluginDeclaration,
    PluginContextMenu,
    ResolvedSlashCommand,
} from './types';

// ── Internal State ──────────────────────────────────────────

const plugins: PluginDeclaration[] = [];
let frozen = false;

/**
 * True only in the Vite dev server, where hot updates re-execute module
 * scopes. Vitest also defines `import.meta.hot`, so exclude tests — they
 * (and builds) keep the strict freeze/duplicate guards.
 */
const HMR_ACTIVE = Boolean(import.meta.hot) && !import.meta.env.TEST;

// ── Registration ────────────────────────────────────────────

let version = 0;

/** Bumped on every registration and clear, so a cache of the tools sees a hot update. */
export function registryVersion(): number {
    return version;
}

/** Register a single plugin. Must be called before the registry is frozen. */
export function registerPlugin(decl: PluginDeclaration): void {
    const existing = plugins.findIndex(p => p.id === decl.id);
    version++;

    // Under Vite HMR, plugin modules re-execute their module scope whenever a
    // hot update's bubble path traverses them — re-registration there is
    // expected, not accidental. Upsert in place instead of throwing: a throw
    // here fails the entire hot update and leaves the page stale (and every
    // later update broken) until a manual refresh.
    if (HMR_ACTIVE) {
        if (existing >= 0) {
            plugins[existing] = decl;
        } else {
            plugins.push(decl);
        }
        return;
    }

    if (frozen) {
        throw new Error(`Cannot register plugin "${decl.id}" after registry is frozen.`);
    }
    if (existing >= 0) {
        throw new Error(`Duplicate plugin id: "${decl.id}"`);
    }
    plugins.push(decl);
}

/**
 * Register multiple tool-only plugins from a ToolDefinition array.
 * Convenience for tool groups (behavior-tools, etc.) that have no
 * slash commands or context menus.
 */
export function registerTools(tools: ToolDefinition[]): void {
    for (const t of tools) {
        registerPlugin({ id: t.tool.name, tool: t });
    }
}

/**
 * Freeze the registry. Called after all plugins are loaded.
 * Prevents accidental late registration.
 */
export function freezeRegistry(): void {
    frozen = true;
}

// ── Queries ─────────────────────────────────────────────────

/** Get all registered plugins. */
export function getPlugins(): readonly PluginDeclaration[] {
    return plugins;
}

/** Get all ToolDefinitions (one per plugin). */
export function getToolDefinitions(): ToolDefinition[] {
    return plugins.map(p => p.tool);
}

/** Look up a plugin by its tool name. */
export function getPluginByToolName(toolName: string): PluginDeclaration | undefined {
    return plugins.find(p => p.tool.tool.name === toolName);
}

/** Get all slash commands, flattened across all plugins. */
export function getSlashCommands(): ResolvedSlashCommand[] {
    const result: ResolvedSlashCommand[] = [];
    for (const p of plugins) {
        if (!p.slashCommands) continue;
        const toolName = p.tool.tool.name;
        for (const cmd of p.slashCommands) {
            result.push({ ...cmd, toolName });
        }
    }
    return result;
}

/** Get all context menu entries, flattened across all plugins. */
export function getContextMenuEntries(): PluginContextMenu[] {
    const result: PluginContextMenu[] = [];
    for (const p of plugins) {
        if (!p.contextMenus) continue;
        result.push(...p.contextMenus);
    }
    return result;
}

// ── Reset ───────────────────────────────────────────────────

/** Clear all plugins and unfreeze. Used by tests. */
export function clearRegistry(): void {
    plugins.length = 0;
    version++;
    frozen = false;
}

/** @deprecated Use clearRegistry(). Kept for test backward compat. */
export const _resetForTesting = clearRegistry;
